// TWR Comparison (REMOVABLE) — overlays the NEW time-weighted return against the OLD
// money-weighted performance and SPY total-return, all rebased to 0% at the window start.
// Kept as its own self-contained tab so it can be deleted once the old module is retired.
import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';
import { tradingService } from '../../services/tradingService';

const ACCOUNT_LS_KEY = 'maghaweer.twr.selected_account_id';   // shared with the TWR tab
const GOLD = '#c9a96e';
const BLUE = '#5b8def';
const RED = '#e0574a';

const pct = (v, dp = 2) => (v == null ? '—' : `${(v * 100).toFixed(dp)}%`);
const signClass = (v) => (v == null ? 'text-gray-400' : v >= 0 ? 'text-emerald-500' : 'text-red-500');
const last = (arr) => (arr && arr.length ? arr[arr.length - 1].cumulative : null);

const TwrComparisonPage = () => {
  const [accounts, setAccounts] = useState(null);
  const [accountId, setAccountId] = useState(() => {
    const s = localStorage.getItem(ACCOUNT_LS_KEY);
    return s ? Number(s) : null;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then(r => {
        const list = r.data?.results || r.data || [];
        setAccounts(list);
        if (accountId == null && list.length) setAccountId(list[0].id);
      })
      .catch(() => setAccounts([]));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accountId != null) localStorage.setItem(ACCOUNT_LS_KEY, String(accountId));
  }, [accountId]);

  useEffect(() => {
    if (accountId == null) return;
    setLoading(true); setError(null);
    tradingService.getPortfolioTwr(accountId)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.error || 'Failed to load comparison'))
      .finally(() => setLoading(false));
  }, [accountId]);

  const twr = data?.series || [];
  const old = data?.old_series || [];
  const spy = data?.spy_series || [];
  const spyLabel = data?.benchmark_label === 'SPX (net of WHT)' ? 'SPY TR (net of WHT)' : 'SPY total return';

  const trace = (arr, name, color, dash) => ({
    x: arr.map(p => p.date), y: arr.map(p => p.cumulative),
    type: 'scatter', mode: 'lines', name,
    line: { color, width: 2, dash: dash || 'solid' },
    hovertemplate: `${name}: %{y:.2%}<extra></extra>`,
  });

  const layout = mergeLayout({
    height: 420, margin: { l: 48, r: 16, t: 12, b: 36 },
    yaxis: { tickformat: '.1%' },
    legend: { orientation: 'h', y: 1.08, x: 0 },
  });

  const rows = [
    { k: 'New TWR (flow-neutral)', v: last(twr), c: GOLD },
    { k: 'Old (money-weighted)', v: last(old), c: BLUE },
    { k: spyLabel, v: last(spy), c: RED },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-serif text-gray-800 dark:text-gray-100">TWR vs Old vs SPY</h2>
          <p className="text-[11px] text-gray-400">
            All rebased to 0% at window start. Temporary side-by-side — remove once the old module is retired. {data?.account_name}
          </p>
        </div>
        {accounts && accounts.length >= 2 && (
          <select value={accountId ?? ''} onChange={e => setAccountId(Number(e.target.value))}
            className="text-sm bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 rounded px-2 py-1">
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="text-sm text-red-500">{error}</div>}
      {loading && !data && <div className="text-sm text-gray-400 italic">Loading…</div>}

      {data && (<>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {rows.map((r) => (
            <div key={r.k} className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3 flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: r.c }} />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 truncate">{r.k}</div>
                <div className={`text-lg font-semibold tabular-nums ${signClass(r.v)}`}>{pct(r.v)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          {(twr.length || old.length || spy.length) ? (
            <Plot
              data={[
                trace(twr, 'New TWR', GOLD),
                trace(old, 'Old (MWR)', BLUE),
                trace(spy, spyLabel, RED, 'dash'),
              ]}
              layout={layout} config={PLOTLY_CONFIG} useResizeHandler
              style={{ width: '100%' }}
            />
          ) : <p className="text-sm text-gray-400 italic">No data yet.</p>}
        </div>

        <p className="text-[10px] text-gray-400">
          Note: historical snapshots predate the dividend-correct shadow equity, so the New-TWR line is
          dividend-blind for days before the TWR system went live; the gap closes going forward.
          {data?.benchmark_label === 'SPX (net of WHT)' && ' The benchmark reinvests SPY distributions net of the same withholding rate applied to the portfolio (30% US-source default).'}
        </p>
      </>)}
    </div>
  );
};

export default TwrComparisonPage;
